import {
  ApplicationConfig,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  inject,
} from '@angular/core';
import { provideRouter, withHashLocation } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { firstValueFrom } from 'rxjs';

import { environment } from '@env';

import {
  authInterceptor,
  localeInterceptor,
  refreshInterceptor,
  toastInterceptor,
} from './core/interceptors';
import { AuthService } from './core/services/auth.service';
import { SessionService } from '@core/services/session.service';
import { routes } from './app.routes';

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideAnimationsAsync(),
    // Hash URLs (/#/login): static hosting has no SPA fallback, so deep links and refresh would 404.
    provideRouter(routes, withHashLocation()),
    // Centralized dialog behavior: no dialog closes on backdrop click or ESC by default.
    // Override per-dialog with `disableClose: false` if a specific modal needs it.
    { provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: { disableClose: true } },
    provideHttpClient(
      // toastInterceptor is outermost so it reacts to the FINAL response: a 401 recovered by
      // refreshInterceptor (expired access token) must not surface a spurious error toast.
      withInterceptors([toastInterceptor, localeInterceptor, authInterceptor, refreshInterceptor]),
    ),
    provideTranslateService({
      lang: environment.defaultLanguage,
      fallbackLang: environment.defaultLanguage,
      loader: provideTranslateHttpLoader({
        prefix: 'assets/i18n/',
        suffix: '.json',
      }),
    }),
    provideAppInitializer(async () => {
      const authService = inject(AuthService);
      // Eagerly created so idle tracking and cross-tab session events run from app start.
      inject(SessionService);
      try {
        await firstValueFrom(authService.loadCurrentUser());
      } catch {
        // Anonymous bootstrap is expected: /auth/me returns 401 without a session.
      }
    }),
  ],
};
